import React, { useCallback, useEffect, useState } from 'react'
import { SkeletonList } from '../../components/Skeleton'
import {
  BackLink,
  Card,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/recordsService'
import { parseUtcDate } from '../../utils/dates'

function fmt(iso) {
  if (!iso) return ''
  return parseUtcDate(iso).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function PrescriptionsPage() {
  const { currentProfile } = usePatient()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const res = await svc.listPrescriptions(currentProfile)
      setData(res)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setData(null)
    refresh()
  }, [refresh])

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <BackLink to="/patient-home/records">Back to Records</BackLink>
        <div>
          <Pill>Records</Pill>
          <PageHeading style={{ marginTop: 12 }}>Prescriptions</PageHeading>
          <PageSubheading>Every Rx across your sessions, printable.</PageSubheading>
        </div>

        {loading && !data ? (
          <SkeletonList count={3} />
        ) : !data?.items?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No prescriptions yet.</p>
            <Muted>Once a doctor writes one, it shows up here with a printable view.</Muted>
          </div>
        ) : (
          <Stack $gap="10px">
            {data.items.map((rx) => (
              <Card key={rx.id}>
                <Row $between $gap="12px" $wrap>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>
                      {rx.practitionerName}
                      <span style={{ color: '#6B7280', fontWeight: 400 }}>
                        {rx.practitionerSpecialty ? ` · ${rx.practitionerSpecialty}` : ''}
                      </span>
                    </div>
                    <Muted>{fmt(rx.issuedAt)}</Muted>
                    {rx.summary && (
                      <Muted style={{ marginTop: 6, color: '#1F2937' }}>{rx.summary}</Muted>
                    )}
                  </div>
                  {rx.printable && <Pill>Printable</Pill>}
                </Row>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
